/**
 * Item Location Balance Service
 *
 * Service for fetching item-bin balance data from the DataInspectionController.
 * Uses the existing ItemBins endpoints to retrieve location balances.
 */

import { AuthService } from './authService';
import { ItemLocationBalanceDto } from '../types/itemLocationBalance';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class ItemLocationBalanceService {
  /**
   * Fetches all item-bin relationships with balances
   * @returns Promise<ItemLocationBalanceDto[]> Array of all item bins
   */
  async getAllItemBins(): Promise<ItemLocationBalanceDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/data-inspection/itembins`
    );

    if (!response.ok) {
      throw new Error(`Failed to get item bins: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Fetches all locations for a specific item
   * @param itemNo - The item number to filter by
   * @returns Promise<ItemLocationBalanceDto[]> Array of item bins for the specified item
   */
  async getItemBinsByItemNo(itemNo: string): Promise<ItemLocationBalanceDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/data-inspection/itembins/item/${encodeURIComponent(itemNo)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get item bins for item ${itemNo}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Fetches all items stored in a specific bin location
   * @param binCode - The bin code to filter by
   * @returns Promise<ItemLocationBalanceDto[]> Array of item bins for the specified location
   */
  async getItemBinsByBinCode(binCode: string): Promise<ItemLocationBalanceDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/data-inspection/itembins/bin/${encodeURIComponent(binCode)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get item bins for bin ${binCode}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }
}

export const itemLocationBalanceService = new ItemLocationBalanceService();
